import React, { FC, useCallback, useState } from "react";
import EditSection from "components/common/ui/EditSection";
import CommonButton from "components/common/ui/CommonButton";
import MenuLoopBasicInfoEditSection from "components/menu/ui/menuLoop/MenuLoopBasicInfoEditSection";
import MenusInLoopEditSection from "components/menu/ui/menuLoop/MenusInLoopEditSection";
import CopyMenuLoopDialog from "components/menu/ui/menuLoop/CopyMenuLoopDialog";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import menuLoopDetailSelector from "store/selectors/menu/menuLoopDetailSelector";
import { menuLoopActions } from "store/slices/menu/menuLoopDetailSlice";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import MenuLoopSpecialDatesEditSection from "components/menu/ui/menuLoop/MenuLoopSpecialDatesEditSection";
import ColoredCircularProgress from "components/common/ui/ColorCircularProgress";
import { css } from "styled-components";
import { Box } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import jspPlatformApi from "client/portals";
import dayjs from "dayjs";

interface MenuLoopEditFormsProps {}

const MenuLoopEditForms: FC<MenuLoopEditFormsProps> = () => {
  const promptSnackBar = usePromptSnackBar();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { menuLoopId } = useParams();
  const editingMenuLoop = useAppSelector(
    menuLoopDetailSelector.editingMenuLoop,
  );
  const isSaving = useAppSelector(menuLoopDetailSelector.isSaving);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const onCreate = useCallback(() => {
    if (editingMenuLoop) {
      dispatch(menuLoopActions.createMenuLoop(editingMenuLoop))
        .unwrap()
        .then((message) => {
          promptSnackBar.setPromptProps({
            isOpen: true,
            message,
            severity: "success",
            autoHeightDuration: 5000,
          });
          navigate(-1);
        })
        .catch(() => {});
    }
  }, [editingMenuLoop, navigate, promptSnackBar, dispatch]);

  const onUpdate = useCallback(() => {
    if (editingMenuLoop && menuLoopId) {
      dispatch(
        menuLoopActions.updateMenuLoop({
          menuLoopId: Number(menuLoopId),
          menuLoop: editingMenuLoop,
        }),
      )
        .unwrap()
        .then((message) => {
          promptSnackBar.setPromptProps({
            isOpen: true,
            message,
            severity: "success",
            autoHeightDuration: 5000,
          });
          navigate(-1);
        })
        .catch(() => {});
    }
  }, [editingMenuLoop, menuLoopId, navigate, promptSnackBar, dispatch]);

  const handleSaveClick = useCallback(() => {
    if (editingMenuLoop) {
      if (Number(menuLoopId)) {
        // Update
        onUpdate();
      } else {
        // Create
        onCreate();
      }
    }
  }, [editingMenuLoop, menuLoopId, onCreate, onUpdate]);

  /**
   * Download the involved foods of this menu loop as a ZIP of CSV files
   * (one CSV per recipe plus an aggregated ingredients.csv)
   */
  const handleExportInvolvedFoodsClick = useCallback(() => {
    if (!Number(menuLoopId)) {
      return;
    }
    setIsExporting(true);
    jspPlatformApi()
      .menuLoop.getMenuLoopByMenuLoopIdInvolvedFoodsDownload({
        path: { menu_loop_id: Number(menuLoopId) },
      })
      .then((res) => {
        const url = window.URL.createObjectURL(
          new Blob([res.data as Blob], { type: "application/zip" }),
        );
        const alink = document.createElement("a");
        alink.href = url;
        alink.download =
          `${editingMenuLoop?.name ?? "menu loop"} involved foods ` +
          `downloaded at ${dayjs().toISOString()}.zip`;
        document.body.appendChild(alink);
        alink.click();
        alink.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: `Download error. ${err}`,
          severity: "error",
        });
      })
      .finally(() => {
        setIsExporting(false);
      });
  }, [editingMenuLoop?.name, menuLoopId, promptSnackBar]);

  const handleCopyClick = useCallback(() => {
    if (editingMenuLoop) {
      setDialogOpen(true);
    }
  }, [editingMenuLoop]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  return (
    <>
      <EditSection>
        <Box display="flex" alignItems="center">
          <CommonButton
            text={isSaving ? "Saving..." : "Save"}
            positive={!isSaving}
            onClick={handleSaveClick}
            disabled={isSaving}
          />
          <CommonButton
            text="Copy"
            positive={!isSaving}
            onClick={handleCopyClick}
            disabled={isSaving}
          />
          {isSaving && (
            <ColoredCircularProgress
              customColor={css`rgb(200, 200, 200)`}
              size={20}
            />
          )}
          {!!Number(menuLoopId) && (
            <Box display="flex" alignItems="center" marginLeft="auto">
              {isExporting && (
                <ColoredCircularProgress
                  customColor={css`rgb(200, 200, 200)`}
                  size={20}
                />
              )}
              <CommonButton
                startIcon={<FileDownloadIcon />}
                text={isExporting ? "Exporting..." : "Export Involved Foods"}
                positive={!isExporting}
                onClick={handleExportInvolvedFoodsClick}
                disabled={isExporting}
              />
            </Box>
          )}
        </Box>
      </EditSection>
      <EditSection header="Menu Loop Basic Information">
        <MenuLoopBasicInfoEditSection />
      </EditSection>
      <EditSection header="Menus in Loop">
        <MenusInLoopEditSection />
      </EditSection>
      <EditSection header="Special Dates (No Publishing)">
        <MenuLoopSpecialDatesEditSection />
      </EditSection>
      <EditSection />
      {editingMenuLoop && (
        <CopyMenuLoopDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          editingMenuLoop={editingMenuLoop}
          menuLoopId={menuLoopId}
        />
      )}
    </>
  );
};

export default MenuLoopEditForms;
